import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Field, FormError } from '../components/ui/field';
import { LoadingScreen } from '../components/ui/spinner';
import { authErrorMessage, useAuthStore } from '../stores/auth-store';

type Mode = 'signIn' | 'signUp';

/** Where the user was heading before ProtectedRoute sent them here. */
interface RedirectState {
  from?: { pathname?: string };
}

function GoogleMark() {
  return (
    <svg className="google-mark" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.35 0-4.34-1.58-5.05-3.71H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.95 10.71a5.4 5.4 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l2.99-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l2.99 2.33C4.66 5.16 6.65 3.58 9 3.58Z"
      />
    </svg>
  );
}

export function LoginPage() {
  const status = useAuthStore((state) => state.status);
  const signInWithGoogle = useAuthStore((state) => state.signInWithGoogle);
  const signInWithEmail = useAuthStore((state) => state.signInWithEmail);
  const signUpWithEmail = useAuthStore((state) => state.signUpWithEmail);
  const location = useLocation();

  const [mode, setMode] = useState<Mode>('signIn');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'google' | 'email' | null>(null);

  if (status === 'loading') {
    return <LoadingScreen label="Restoring your session..." />;
  }

  if (status === 'authenticated') {
    const target = (location.state as RedirectState | null)?.from?.pathname ?? '/dashboard';
    // `replace` so the back button does not land on the login page again.
    return <Navigate to={target} replace />;
  }

  async function handleGoogle() {
    setError(null);
    setBusy('google');
    try {
      await signInWithGoogle();
    } catch (cause) {
      setError(authErrorMessage(cause));
    } finally {
      setBusy(null);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setBusy('email');
    try {
      if (mode === 'signUp') {
        await signUpWithEmail(email, password, displayName);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (cause) {
      setError(authErrorMessage(cause));
    } finally {
      setBusy(null);
    }
  }

  const isSignUp = mode === 'signUp';

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-card__brand">LiveForge</h1>
        <p className="auth-card__purpose">
          A self-hosted live streaming studio for producing multi-camera broadcasts with guests,
          graphics, and YouTube output.
        </p>
        <p className="auth-card__subtitle">
          {isSignUp ? 'Create an account to start producing.' : 'Sign in to your studio.'}
        </p>

        <FormError>{error}</FormError>

        <Button variant="secondary" block onClick={handleGoogle} loading={busy === 'google'}>
          <GoogleMark />
          Continue with Google
        </Button>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <form className="stack" onSubmit={handleSubmit}>
          {isSignUp ? (
            <Field label="Display name">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="text"
                  autoComplete="name"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                />
              )}
            </Field>
          ) : null}

          <Field label="Email">
            {(id) => (
              <input
                id={id}
                className="input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>

          <Field label="Password" hint={isSignUp ? 'At least 6 characters.' : undefined}>
            {(id) => (
              <input
                id={id}
                className="input"
                type="password"
                required
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>

          <Button type="submit" variant="primary" block loading={busy === 'email'}>
            {isSignUp ? 'Create account' : 'Sign in'}
          </Button>
        </form>

        <p className="auth-toggle">
          {isSignUp ? 'Already have an account?' : 'No account yet?'}{' '}
          <button
            type="button"
            className="button button--ghost button--small"
            onClick={() => {
              setMode(isSignUp ? 'signIn' : 'signUp');
              setError(null);
            }}
          >
            {isSignUp ? 'Sign in' : 'Create one'}
          </button>
        </p>

        {/*
          Not decoration. Google's OAuth review requires the privacy policy to
          be linked from the app's homepage, and for a signed-out visitor -
          which is what a reviewer is - this page is the homepage.
        */}
        <p className="auth-legal muted">
          <Link to="/privacy">Privacy Policy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms">Terms of Service</Link>
        </p>
      </div>
    </div>
  );
}
